import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Apartment as TenantIcon,
  CheckCircle as ActiveIcon,
  People as UsersIcon,
  Description as DocsIcon,
  TrendingUp as MrrIcon,
  Payments as ReceivedIcon,
  NetworkCheck as MistralIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
} from 'recharts';

import StatCard from '../../components/admin/StatCard';
import {
  getAnalyticsOverview,
  getTenantAnalytics,
  getUsageSeries,
  AnalyticsOverview,
  TenantAnalyticsRow,
  UsagePoint,
} from '../../services/adminAnalytics';
import { getRevenue, RevenueSummary } from '../../services/adminBilling';
import { getMistralUsage, MistralUsage } from '../../services/adminMistral';

const PALETTE = ['#F26F21', '#F5B800', '#2E7D32', '#1976D2', '#8E24AA', '#00897B', '#C62828'];

const ChartCard: React.FC<{ title: string; children: React.ReactNode; height?: number }> = ({ title, children, height = 280 }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Typography variant="subtitle1" fontWeight={600} mb={1}>{title}</Typography>
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children as any}
      </ResponsiveContainer>
    </Box>
  </Paper>
);

const AdminDashboardPage: React.FC = () => {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [tenants, setTenants] = useState<TenantAnalyticsRow[]>([]);
  const [usage, setUsage] = useState<UsagePoint[]>([]);
  const [revenue, setRevenue] = useState<RevenueSummary | null>(null);
  const [mistral, setMistral] = useState<MistralUsage | null>(null);
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadBase = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [ov, tn] = await Promise.all([getAnalyticsOverview(), getTenantAnalytics()]);
      setOverview(ov);
      setTenants(tn);
      // Non-critical extras — don't block the dashboard if they fail.
      getRevenue(12).then(setRevenue).catch(() => {});
      getMistralUsage(30).then(setMistral).catch(() => {});
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load analytics. Please retry.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsage = useCallback(async (tid: string) => {
    try {
      const res = await getUsageSeries(tid === 'all' ? null : tid, 30);
      setUsage(res.series);
    } catch (e) {
      setUsage([]);
    }
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadUsage(tenantFilter); }, [tenantFilter, loadUsage]);

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }
  if (error || !overview) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={8}>
        <Typography color="error">{error || 'No analytics available.'}</Typography>
        <Button variant="outlined" onClick={loadBase}>Retry</Button>
      </Box>
    );
  }

  const planData = overview.tenants.by_plan
    .filter((p) => p.count > 0 || overview.tenants.total === 0)
    .map((p) => ({ name: p.name || p.code || '—', value: p.count }));
  const statusData = Object.entries(overview.tenants.by_status).map(([k, v]) => ({ name: k, value: v }));
  const roleData = Object.entries(overview.users.by_role).map(([k, v]) => ({ name: k, value: v }));
  const usersPerTenant = tenants.map((t) => ({ name: t.name, users: t.users }));

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Dashboard</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Platform-wide analytics across all tenants.</Typography>

      {/* KPI row */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Tenants" value={overview.tenants.total} icon={<TenantIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Active tenants" value={overview.tenants.by_status['active'] || 0} icon={<ActiveIcon />} color="#2E7D32" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Users" value={overview.users.total} icon={<UsersIcon />} color="#1976D2" sub={`${overview.users.active} active`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Documents this month" value={overview.usage.documents_this_month.toLocaleString()} icon={<DocsIcon />} color="#F5B800" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="MRR (active subscriptions)" value={`€${(revenue?.mrr ?? 0).toLocaleString()}`} icon={<MrrIcon />} color="#2E7D32" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Received this month" value={`€${(revenue?.received_this_month ?? 0).toLocaleString()}`} icon={<ReceivedIcon />} color="#F26F21" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Mistral requests (30d)" value={(mistral?.total_requests ?? 0).toLocaleString()} icon={<MistralIcon />} color="#1976D2" sub={`~€${(mistral?.est_cost ?? 0).toFixed(2)} est. cost`} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {/* Tenants by plan */}
        <Grid item xs={12} md={4}>
          <ChartCard title="Tenants by plan">
            <PieChart>
              <Pie data={planData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {planData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <ReTooltip />
              <Legend />
            </PieChart>
          </ChartCard>
        </Grid>

        {/* Tenants by status */}
        <Grid item xs={12} md={4}>
          <ChartCard title="Tenants by status">
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} label>
                {statusData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <ReTooltip />
              <Legend />
            </PieChart>
          </ChartCard>
        </Grid>

        {/* Users by role */}
        <Grid item xs={12} md={4}>
          <ChartCard title="Users by role">
            <BarChart data={roleData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <ReTooltip />
              <Bar dataKey="value" fill="#1976D2" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartCard>
        </Grid>

        {/* Users per tenant */}
        <Grid item xs={12} md={5}>
          <ChartCard title="Users per tenant">
            <BarChart data={usersPerTenant} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} fontSize={12} />
              <YAxis type="category" dataKey="name" width={120} fontSize={12} />
              <ReTooltip />
              <Bar dataKey="users" fill="#F26F21" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ChartCard>
        </Grid>

        {/* Usage over time + tenant switch */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight={600}>Document processing (30 days)</Typography>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="tenant-filter">Tenant</InputLabel>
                <Select labelId="tenant-filter" label="Tenant" value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)}>
                  <MenuItem value="all">All tenants</MenuItem>
                  {tenants.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={usage}>
                  <defs>
                    <linearGradient id="usageFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F26F21" stopOpacity={0.7} />
                      <stop offset="95%" stopColor="#F26F21" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <ReTooltip />
                  <Legend />
                  <Area type="monotone" dataKey="total" name="Total" stroke="#F26F21" fill="url(#usageFill)" />
                  <Area type="monotone" dataKey="failed" name="Failed" stroke="#C62828" fill="#C6282822" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboardPage;
